import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { addMinutes } from 'date-fns';
import { of, race } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ParamsModel } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    ChangeLogState,
    getChangesData,
    getPersistedChangesData,
    getRevertedChangesForModel,
    reconcileEntityUpdatesWithChanges,
} from '@mona/pdms/data-access-changelog';
import { withCurrentEncounterId, currentEncounterId, isEmptyEncounterId } from '@mona/pdms/data-access-combined';
import { isValidDateRange } from '@mona/shared/date';
import { Logger } from '@mona/shared/logger';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { selectCurrentRoute, selectRouteData } from '@mona/store';
import { VitalSignsApi } from '../../infrastructure';
import { VitalSignsAction } from '../actions';
import { selectAllVitals, selectVitalsIsLoading } from '../selectors';

/**
 * Vital signs effects
 */
@Injectable()
export class VitalSignsEffects {
    private logger = new Logger();
    /**
     * Load Vital signs effect
     */
    loadVitalSigns$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VitalSignsAction.loadVitalSigns),
            withCurrentEncounterId(),
            switchMap(([{ params }, encounter_id]) =>
                race(
                    isEmptyEncounterId(this.store$).pipe(
                        take(1),
                        map(() => VitalSignsAction.loadVitalSignsCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.vitalSignsApi.getPageableVitalSigns({ ...params, encounter_id }).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store$), encounter_id),
                        withLatestFrom(this.store$.select(ChangeLogSelectors.getChangesMap)),
                        map(([data, changesMap]) => {
                            const changes = changesMap['VitalSign'] || [];
                            if (changes.length) {
                                const { results } = data;
                                data.results = applyInstancesChanges(results, changes) as any;
                            }
                            return VitalSignsAction.loadVitalSignsSuccess(data);
                        }),
                        catchError(error => of(VitalSignsAction.loadVitalSignsFailed({ error }))),
                        defaultIfEmpty(
                            VitalSignsAction.loadVitalSignsCancelled({ reason: 'encounter-changed' }) as any,
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Load Vital signs limited for date range if date range is valid, otherwise reload all
     */
    onDateRangeChanged$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(VitalSignsAction.setDateRange),
                withCurrentEncounterId(),
                map(([{ dateRange }, encounter_id]) => {
                    const params: ParamsModel = {
                        encounter_id,
                    };
                    if (isValidDateRange(dateRange)) {
                        params.start_date = dateRange.start.toISOString();
                        params.end_date = addMinutes(dateRange.end, 1).toISOString();
                        this.store$.dispatch(VitalSignsAction.loadVitalSigns({ params }));
                    }
                }),
            ),
        { dispatch: false },
    );

    // On savechange action toggle loading to avoid multiple documentations on slow network
    onSaveChangeActionToggleLoading$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.action),
                withLatestFrom(this.store$.select(selectCurrentRoute)),
                filter(([action, route]) => route && route.url?.includes('vital-signs')),
                tap(() => this.store$.dispatch(VitalSignsAction.setLoadingAction({ isLoading: true }))),
            ),
        { dispatch: false },
    );

    /** Handle save failures - disable loading state */
    onChangeSaveFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.saveChangeAction.failedAction),
                tap(() => this.store$.dispatch(VitalSignsAction.setLoadingAction({ isLoading: false }))),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(
                    this.store$.select(selectAllVitals),
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                ),
                tap(([action, allVitalsData, changesMap]) => {
                    const changes = changesMap['VitalSign'] || [];
                    const reverted = getRevertedChangesForModel(action, 'VitalSign');
                    const data = reconcileEntityUpdatesWithChanges(allVitalsData, changes, reverted);

                    if (data.length) {
                        this.store$.dispatch(VitalSignsAction.upsertVitalSigns({ data }));
                    }
                    this.store$.dispatch(VitalSignsAction.setLoadingAction({ isLoading: false }));
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.store$.select(selectAllVitals),
                    this.store$.select(ChangeLogSelectors.getChangesMap),
                ),
                tap(([, data, changesMap]) => {
                    const changes = changesMap['VitalSign'] || [];

                    if (changes.length) {
                        const { toRemove, toUpdate } = getPersistedChangesData(data, changes);

                        this.store$.dispatch(
                            VitalSignsAction.addChanges({
                                toUpdateEntities: toUpdate || [],
                                toRemoveIds: toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    onChangeReverted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                tap(action => {
                    const changes = 'change' in action ? [action.change] : action.changes;
                    const vitalSignCreates = changes
                        .filter(c => c.model === 'VitalSign' && c.operation === 'create')
                        .map(c => c.modelId);

                    if (vitalSignCreates.length) {
                        this.store$.dispatch(VitalSignsAction.removeVitalSigns({ ids: vitalSignCreates }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param actions$ Actions
     * @param store$
     * @param vitalSignsApi VitalSignsApi
     */
    constructor(
        private actions$: Actions,
        private store$: Store<{ changelogData: ChangeLogState }>,
        private vitalSignsApi: VitalSignsApi,
    ) {}
}
